import { useEffect, useState } from "react";
import "./sidebar.scss";
import { auth, db } from "../../firebase";
import { doc, getDoc, collection, onSnapshot } from "firebase/firestore";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import LogoutIcon from "@mui/icons-material/Logout";
import "./sidebardark.scss";
import "./loading.scss";
import { 
  WiDaySunny, WiNightClear, WiDayCloudy, WiNightAltCloudy, 
  WiCloudy, WiFog, WiRain, WiDayRain, WiNightAltRain,
  WiThunderstorm, WiSnow, WiDayShowers, WiNightAltShowers
} from "react-icons/wi";

const Sidebar = () => {
  const [data, setData] = useState([]);
  const [darkMode, setDarkMode] = useState(false);
  const [weatherData, setWeatherData] = useState(null);
  const [forecastList, setForecastList] = useState(null);

  // Map OpenWeatherMap icon codes to react-icons weather icons
  const getWeatherIcon = (iconCode, size = 85) => {
    const iconMap = {
      '01d': <WiDaySunny size={size} />,
      '01n': <WiNightClear size={size} />,
      '02d': <WiDayCloudy size={size} />,
      '02n': <WiNightAltCloudy size={size} />,
      '03d': <WiCloudy size={size} />,
      '03n': <WiCloudy size={size} />,
      '04d': <WiCloudy size={size} />,
      '04n': <WiCloudy size={size} />,
      '09d': <WiDayShowers size={size} />,
      '09n': <WiNightAltShowers size={size} />,
      '10d': <WiDayRain size={size} />,
      '10n': <WiNightAltRain size={size} />,
      '11d': <WiThunderstorm size={size} />,
      '11n': <WiThunderstorm size={size} />,
      '13d': <WiSnow size={size} />,
      '13n': <WiSnow size={size} />,
      '50d': <WiFog size={size} />,
      '50n': <WiFog size={size} />,
    };
    return iconMap[iconCode] || <WiDaySunny size={size} />;
  };

  const [loading, setLoading] = useState(true);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [stationLogo, setStationLogo] = useState("");
  const [version, setVersion] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const updateDarkMode = () => {
      const hours = currentTime.getHours();
      setDarkMode(hours >= 17 || hours < 8); // Step 2
    };

    const intervalId = setInterval(updateDarkMode, 60 * 1000); // Update every minute
    updateDarkMode(); // Initial update

    return () => clearInterval(intervalId);
  }, [currentTime]);


  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const versionDocRef = doc(db, "Stationboards", "appversion");
        const versionDocSnapshot = await getDoc(versionDocRef);
        if (versionDocSnapshot.exists()) {
          const versionData = versionDocSnapshot.data();
          console.log("Fetched data from Stationboards:", versionData);
          if (versionData && versionData.Version) {
            if (!version) {
              // Initialize version state if it's empty
              setVersion(versionData.Version);
            } else if (versionData.Version > version) {
              setVersion(versionData.Version);
              window.location.reload(); // Reload page if fetched version is higher
            }
          }
        } else {
          console.log("Document does not exist!");
        }
      } catch (error) {
        console.error("Error fetching data from Stationboards:", error);
      }
    };

    fetchVersion(); // Fetch version once when the component mounts

    const intervalId = setInterval(fetchVersion, 60 * 60 * 1000); // Check every hour

    return () => clearInterval(intervalId);
  }, [version]);

  
  useEffect(() => {
    async function fetchWeatherData() {
      try {
        const apiKey = "50229c69ed23500083e0004c56b83be4"; // Replace with your OpenWeatherMap API key
        const apiUrl = `https://api.openweathermap.org/data/2.5/weather?lat=${currentLocation.lat}&lon=${currentLocation.lng}&appid=${apiKey}&units=imperial`;

        const response = await axios.get(apiUrl);
        console.log('Weather data received:', response.data);
        setWeatherData(response.data);
        fetchWeatherHistory();
      } catch (error) {
        console.error("Error fetching weather data:", error);
        setLoading(false); // Set loading to false even on error
      }
    }
    async function fetchWeatherHistory() {
      try {
        const apiKey = "50229c69ed23500083e0004c56b83be4"; // Replace with your OpenWeatherMap API key
        const apiUrl = `https://api.openweathermap.org/data/2.5/forecast?lat=${currentLocation.lat}&lon=${currentLocation.lng}&appid=${apiKey}&units=imperial`;

        const response = await axios.get(apiUrl);
        const list = response.data.list;
        
        // Group forecast data by date and calculate actual daily highs and lows
        const dailyData = {};
        
        list.forEach((ele) => {
          const date = ele.dt_txt.split(" ")[0];
          
          if (!dailyData[date]) {
            dailyData[date] = {
              dt_txt: ele.dt_txt,
              temps: [],
              weather: ele.weather,
              main: {
                temp_max: ele.main.temp,
                temp_min: ele.main.temp
              }
            };
          }
          
          // Track all temperatures for this day
          dailyData[date].temps.push(ele.main.temp);
          
          // Update max and min
          if (ele.main.temp > dailyData[date].main.temp_max) {
            dailyData[date].main.temp_max = ele.main.temp;
          }
          if (ele.main.temp < dailyData[date].main.temp_min) {
            dailyData[date].main.temp_min = ele.main.temp;
          }
        });
        
        // Convert to array and sort by date
        const forecast = Object.values(dailyData).sort((a, b) => 
          new Date(a.dt_txt) - new Date(b.dt_txt)
        );
        
        setForecastList(forecast);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching weather data:", error);
        setLoading(false); // Set loading to false even on error
      }
    }

    console.log('Weather useEffect triggered, currentLocation:', currentLocation);
    if (currentLocation) {
      console.log('Fetching weather for location:', currentLocation);
      fetchWeatherData();
      setInterval(() => {
        fetchWeatherData();
      }, 60 * 60 * 1000); // one hour
    }
  }, [currentLocation]);

  useEffect(() => {
    auth.onAuthStateChanged(async (currentUser) => {
      console.log('Auth state changed, currentUser:', currentUser?.uid);
      if (!currentUser) {
        console.error('No authenticated user');
        return;
      }
      const userDocRef = doc(db, "users", currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      console.log('User doc data:', userDoc.data());
      console.log('User agency:', userDoc.data()?.agency);
      // Fetch data for Column 1 from Firestore
      const fetchData = async (user) => {
        const column1CollectionRef = collection(
          db,
          user.agency + " Station Board"
        ); // Replace with your Firestore collection reference

        onSnapshot(column1CollectionRef, (querySnapshot) => {
          console.log('Station Board snapshot received, docs count:', querySnapshot.docs.length);
          const data = querySnapshot.docs.map((doc) => doc.data());
          console.log('Station Board data[0]:', data[0]);
          setData(data[0]);
          
          // Check for coordinates map first
          if (data[0]?.coordinates?.latitude && data[0]?.coordinates?.longitude) {
            const lat = parseFloat(data[0].coordinates.latitude);
            const lng = parseFloat(data[0].coordinates.longitude);
            console.log('Using stored coordinates:', { lat, lng });
            setCurrentLocation({ lat, lng });
          } else {
            // Fallback: Use Nominatim (OpenStreetMap) free geocoding service with US country restriction
            console.log('No stored coordinates, falling back to geocoding zip:', data[0].zip);
            const apiUrl = `https://nominatim.openstreetmap.org/search?format=json&q=${data[0].zip}&countrycodes=us&limit=1`;
            fetch(apiUrl)
              .then((response) => response.json())
              .then((geoData) => {
                if (geoData && geoData.length > 0) {
                  const lat = parseFloat(geoData[0].lat);
                  const lng = parseFloat(geoData[0].lon);
                  console.log('Geocoded coordinates:', { lat, lng });
                  setCurrentLocation({ lat, lng });
                } else {
                  console.error("Geocoding failed for the provided zip code using Nominatim.");
                }
              })
              .catch((error) => {
                console.error("Error fetching geocoding data:", error);
              });
          }
        });
      };

      const fetchAgencySettings = async (user) => {
        const docPath = `${user.agency} Settings/Info`;
        console.log('Attempting to fetch Agency Settings from:', docPath);
        const settingsDocRef = doc(db, user.agency + " Settings", "Info");

        // Fallback function to fetch logo from CRM/{agency}
        const fetchLogoFromCRM = async () => {
          const crmDocPath = `CRM/${user.agency}`;
          console.log('Falling back to CRM for logo:', crmDocPath);
          const crmDocRef = doc(db, "CRM", user.agency);
          
          try {
            const crmSnapshot = await getDoc(crmDocRef);
            if (crmSnapshot.exists()) {
              const crmData = crmSnapshot.data();
              console.log('CRM data:', crmData);
              console.log('CRM logo value:', crmData?.logo);
              if (crmData?.logo) {
                setStationLogo(crmData.logo);
              } else {
                console.error('No logo found in CRM document either');
              }
            } else {
              console.error('CRM document does not exist');
            }
          } catch (error) {
            console.error('Error fetching CRM logo:', error);
          }
        };

        onSnapshot(settingsDocRef, (docSnapshot) => {
          console.log('Agency Settings snapshot received, exists:', docSnapshot.exists());
          if (docSnapshot.exists()) {
            const data = docSnapshot.data();
            console.log('Agency Settings data:', data);
            console.log('Logo value:', data?.logo);
            if (data?.logo) {
              setStationLogo(data.logo);
            } else {
              console.log('No logo in Agency Settings, trying CRM fallback...');
              fetchLogoFromCRM();
            }
          } else {
            console.log('Agency Settings/Info does not exist, trying CRM fallback...');
            fetchLogoFromCRM();
          }
        }, (error) => {
          console.error('Error fetching Agency Settings:', error);
          fetchLogoFromCRM();
        });
      };

      // Call the fetch functions to load data
      fetchData(userDoc.data());

      fetchAgencySettings(userDoc.data());
    });
  }, []);

  useEffect(() => {
    // This function will be called once when the component mounts
    const intervalId = setInterval(() => {
      // Update the current time every second
      setCurrentTime(new Date());
    }, 1000);

    // Clean up the interval when the component unmounts
    return () => clearInterval(intervalId);
  }, []);

  // Fallback timeout to prevent indefinite loading
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (loading) {
        console.warn('Sidebar loading timeout reached, forcing loading to false');
        setLoading(false);
      }
    }, 15000); // 15 second timeout

    return () => clearTimeout(timeoutId);
  }, [loading]);

  function getForecastElements() {
    let elements = [];
    if (!forecastList || forecastList.length < 4) {
      return <div style={{textAlign: 'center', padding: '10px'}}>Loading forecast...</div>;
    }
    for (let i = 1; i < 4; i++) {
      let date = new Date(forecastList[i].dt_txt);
      let dateEle = (
        <p className="day">
          {date.toLocaleDateString("en-us", { weekday: "long" })}
        </p>
      );
      let weatherEle = (
        <div className="weather">
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {getWeatherIcon(forecastList[i].weather[0].icon, 50)}
          </div>
          <p>
            {Math.round(forecastList[i].main.temp_max)}
            {"\u00b0"}
          </p>
          <p>
            {Math.round(forecastList[i].main.temp_min)}
            {"\u00b0"}
          </p>
        </div>
      );

      elements.push(
        <div className="weather-day">
          {dateEle}
          {weatherEle}
        </div>
      );
    }

    return elements;
  }
  const hours = currentTime.getHours();
  const minutes = currentTime.getMinutes();
  // Show loading indicator instead of blank screen
  if (loading) {
    return (
      <div className={`sidebar ${darkMode ? "dark-mode" : ""}`}>
        <div className="loading-container" style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column'}}>
          <div style={{fontSize: '18px', marginBottom: '20px'}}>Loading...</div>
          <div style={{width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: '4px solid #3498db', borderRadius: '50%', animation: 'spin 1s linear infinite'}}></div>
        </div>
      </div>
    );
  }

  // Only require essential data - weather and logo have fallbacks
  if (!data) {
    console.log('=== Sidebar Data Debug ===' );
    console.log('data:', data);
    console.log('=========================');
    return (
      <div className={`sidebar ${darkMode ? "dark-mode" : ""}`}>
        <div className="error-container" style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', padding: '20px'}}>
          <div style={{fontSize: '16px', textAlign: 'center', marginBottom: '20px'}}>Unable to load sidebar data</div>
          <div style={{fontSize: '14px', textAlign: 'center', opacity: '0.7'}}>Please check your internet connection and try refreshing the page</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`sidebar ${darkMode ? "dark-mode" : ""}`}>
      <div className="logout">
        <div 
          className="logout-icon-container"
          onClick={() => {
            auth.signOut();
            window.localStorage.removeItem("user");
            navigate("/login");
          }}
          title="Logout"
        >
          <LogoutIcon className="logout-icon" />
        </div>
        <div className="version-label">
          <p>Version: {version}</p>
        </div>
      </div>
      <div className="logo">
        {stationLogo ? (
          <img src={stationLogo} alt="Station Logo" />
        ) : (
          <div style={{fontSize: '14px', opacity: '0.7', textAlign: 'center'}}>Logo unavailable</div>
        )}
      </div>
      <div className={`details ${darkMode ? "dark-mode" : ""}`}>
        <h2>{data?.name || 'Loading...'}</h2>
        <h4>{data?.description || 'Loading...'}</h4>
      </div>
      <div className="weather-container">
        {weatherData ? (
          <>
            <div className="temperature">
              {getWeatherIcon(weatherData?.weather?.[0]?.icon || '01d', 85)}{" "}
              <p>
                {weatherData?.main?.temp ? Math.round(weatherData.main.temp) : '--'}
                {"\u00b0"}
              </p>
            </div>
            <div className="location">
              <p>{weatherData?.name || ''}</p>
            </div>
          </>
        ) : (
          <div style={{textAlign: 'center', padding: '20px', opacity: '0.7'}}>
            <p>WX information not available</p>
          </div>
        )}
      </div>
      <div className="forecast-list">
        {weatherData ? getForecastElements() : null}
      </div>
      <div className={`time ${darkMode ? "dark-mode" : ""}`}>
        <p>
          {hours < 10 ? `0${hours}` : hours}:
          {minutes < 10 ? `0${minutes}` : minutes}
        </p>
        
      </div>
      <div className="date">
        <p>
          {currentTime.toLocaleString("en-us", {
            weekday: "short",
            month: "short",
            day: "numeric",
          })}
        </p>
      </div>
    </div>
  );
};

export default Sidebar;
